<template>
  <div class="fill-height eledio-theme">
    <v-row justify="center" class="w-full">
      <v-col cols="12" xs="12" md="8" lg="7" class="w-full">
        <div class="site-header d-flex align-center justify-center mb-6">
          <IXSolveLogo class="logo-full" width="134" height="30" />

          <!-- Language Selector -->
          <div class="language-selector ml-4">
            <LocaleSelect />
          </div>
        </div>

        <v-card elevation="0" class="payment-card pa-6 w-full">
          <h1 class="text-h4 font-weight-bold text-center mb-6">
            {{ $t('global.transaction.paymentTitle') }}
          </h1>

          <!-- Subscription Info Section -->
          <v-card-text class="subscription-info py-4">
            <v-overlay :value="loading" absolute>
              <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>

            <v-card outlined class="mb-5 subscription-details">
              <v-card-title class="subtitle-1 font-weight-bold">
                {{ $t('global.subscription.mobileConnectionM2M') }}
              </v-card-title>

              <v-card-text>
                <!-- Status Badge -->
                <div
                  v-if="subscriptionData"
                  class="status-badge"
                  :class="{ expired: isSubscriptionExpired }"
                >
                  {{
                    !isMoreThan30DaysLater(subscriptionData?.expirationDate)
                      ? $t('global.subscription.subscriptionWillEndSoon', {
                          days: daysUntilExpiration,
                        })
                      : $t('global.subscription.activeSubscription')
                  }}
                </div>

                <div class="subscription-dates mt-4">
                  <div class="d-flex justify-space-between mb-2">
                    <span class="font-weight-medium">{{
                      $t('global.subscription.subscriptionFrom')
                    }}</span>
                    <span>{{ formattedStartDate }}</span>
                  </div>
                  <div class="d-flex justify-space-between mb-2">
                    <span class="font-weight-medium">{{
                      $t('global.subscription.subscriptionExpires')
                    }}</span>
                    <span>{{ formattedExpirationDate }}</span>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span class="font-weight-medium">{{ $t('global.device') }}:</span>
                    <span>{{ deviceName }}</span>
                  </div>
                </div>

                <div class="text-center mt-4">
                  <a class="subscription-service-link" :href="localizedLink" target="_blank">
                    {{ $t('global.moreInfoAboutSubscriptionServices') }}
                  </a>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>

          <h2 class="section-title font-weight-bold py-2">
            {{ $t('global.billing.billingInformation') }}
          </h2>

          <v-alert v-if="error" type="error" class="mb-4" dismissible @input="error = ''">
            {{ error }}
          </v-alert>

          <v-form ref="form" v-model="valid" class="billing-form">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.firstName"
                  :label="$t('global.billing.firstName')"
                  :rules="[rules.required]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.lastName"
                  :label="$t('global.billing.lastName')"
                  :rules="[rules.required]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.company"
                  :label="$t('global.billing.company')"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.companyId"
                  :label="$t('global.billing.companyId')"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.vat"
                  :label="$t('global.billing.vat')"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.email"
                  :label="$t('global.billing.email')"
                  :rules="[rules.required, rules.email]"
                  type="email"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.phonePrefix"
                  :items="phonePrefixes"
                  :label="$t('global.billing.phonePrefix')"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-select>
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="form.phoneNumber"
                  :label="$t('global.billing.phone')"
                  :rules="[rules.phone]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="form.country"
                  :items="countryItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('global.billing.country')"
                  :rules="[rules.required]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.street"
                  :label="$t('global.billing.street')"
                  :rules="[rules.required]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.streetNumber"
                  :label="$t('global.billing.streetNumber')"
                  :rules="[rules.numeric]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.apartmentNumber"
                  :label="$t('global.billing.apartmentNumber')"
                  :rules="[rules.numeric]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.zipCode"
                  :label="$t('global.billing.zipCode')"
                  :rules="[rules.required, rules.numeric]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.city"
                  :label="$t('global.billing.city')"
                  :rules="[rules.required]"
                  filled
                  rounded
                  dense
                  hide-details="auto"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Currency and Price Section -->
            <div class="price-section pa-4 mt-4 mb-8 text-center">
              <h3 class="mb-4 font-weight-medium">{{ $t('global.selectedCurrency') }}</h3>
              <div class="currency-buttons">
                <v-btn-toggle v-model="selectedCurrency" mandatory dense class="currency-toggle">
                  <v-btn
                    v-for="currency in availableCurrencies"
                    :key="currency"
                    :value="currency"
                    class="currency-btn"
                    @click="updateDisplayedPrice"
                  >
                    {{ currency }}
                  </v-btn>
                </v-btn-toggle>
              </div>

              <div class="price-display mt-6">
                <span class="price-label">{{ $t('global.price') }}:</span>
                <span class="price-value">{{ displayedPrice }} {{ selectedCurrency }}</span>
              </div>
            </div>

            <!-- Submit button -->
            <div class="submit-container text-center my-8">
              <v-btn
                color="#0073e6"
                x-large
                rounded
                min-width="200"
                height="56"
                @click="submitForm"
                :disabled="!valid || loading"
                class="payment-button white--text"
              >
                <v-icon left size="24">mdi-credit-card</v-icon>
                <span class="font-weight-bold text-h6">{{ $t('global.confirm') }}</span>
              </v-btn>
            </div>
          </v-form>
        </v-card>

        <!-- <div class="footer text-center py-6">
          <p class="copyright">© {{ new Date().getFullYear() }} Eledio</p>
        </div> -->
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { subscriptionRenew } from '@/graphql/mutations/subscriptionRenew'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import { subscriptionStatus } from '@/graphql/query/subscriptionStatus'
import { countryCodesWithName } from '@/config/countryCodes'
import LocaleSelect from '@/components/LocaleSelect'
import IXSolveLogo from '@/assets/images/logo-ixsolve.svg'

export default {
  name: 'PublicSubscriptionPayment',
  components: {
    LocaleSelect,
    IXSolveLogo,
  },

  data() {
    return {
      valid: false,
      loading: false,
      error: '',
      deviceName: '',
      subscriptionInfo: null,
      subscriptionData: null,
      form: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phonePrefix: '+420',
        phoneNumber: '',
        country: 'CZ',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        companyId: '',
        vat: '',
      },
      phonePrefixes: ['+420', '+421', '+48', '+1', '+44', '+49', '+33', '+39', '+34', '+43'],
      countryItems: Object.entries(countryCodesWithName).map(([code, name]) => ({
        text: name,
        value: code,
      })),
      availableCurrencies: ['CZK', 'EUR', 'USD'], // Default currencies
      selectedCurrency: 'CZK', // Default selected currency
      displayedPrice: '0.00', // Price to display based on selected currency
      prices: [], // Store the prices data
      rules: {
        required: (value) => !!value || this.$t('global.formValidation.required'),
        phone: (value) =>
          !value || /^\d{9,15}$/.test(value) || this.$t('global.formValidation.notPhoneNumber'),
        numeric: (value) => !value || /^\d+$/.test(value) || this.$t('global.invalidNumeric'),
        email: (value) => /.+@.+\..+/.test(value) || this.$t('global.formValidation.notEmail'),
      },
    }
  },

  computed: {
    deviceId() {
      return this.$route.params.id
    },
    token() {
      return this.$route.query.token
    },
    isSubscriptionValid() {
      return this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'valid'
    },
    isSubscriptionExpired() {
      return this.subscriptionData?.subscriptionStatus?.toLowerCase() === 'expired'
    },
    formattedStartDate() {
      return this.subscriptionData?.startDate
        ? this.formatDate(parseInt(this.subscriptionData.startDate))
        : '-'
    },
    formattedExpirationDate() {
      return this.subscriptionData?.expirationDate
        ? this.formatDate(parseInt(this.subscriptionData.expirationDate))
        : '-'
    },
    localizedLink() {
      const lang = this.$i18n.locale
      return `https://www.eledio.com/${lang}/subscription/`
    },
    daysUntilExpiration() {
      if (!this.subscriptionData?.expirationDate) return 0
      const now = new Date()
      const expirationDate = new Date(parseInt(this.subscriptionData.expirationDate))
      const diffTime = Math.abs(expirationDate - now)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    },
  },

  created() {
    // Set initial language based on URL parameter
    const urlLang = new URLSearchParams(window.location.search).get('lang')
    if (urlLang && ['cs', 'en', 'de', 'pl'].includes(urlLang)) {
      this.$store.commit('changeLocale', urlLang)
    }

    this.fetchData()
  },

  watch: {
    '$store.state.locale'() {
      // Refresh country items when language changes
      this.countryItems = Object.entries(countryCodesWithName).map(([code, name]) => ({
        text: this.$t(`global.countryCodes.${code.toLowerCase()}`) || name,
        value: code,
      }))
      // Refresh data
      this.fetchData()
    },
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'

      try {
        const date = new Date(timestamp)
        if (isNaN(date.getTime())) return '-'
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(date)
      } catch (error) {
        console.error('Error formatting date:', error)
        return '-'
      }
    },

    isMoreThan30DaysLater(timestamp) {
      if (!timestamp) return false
      const now = new Date()
      const futureDate = new Date()
      futureDate.setDate(now.getDate() + 30)
      return parseInt(timestamp) > futureDate.getTime()
    },

    async fetchData() {
      this.loading = true
      try {
        const token = this.token
        const apolloClient = this.$apollo.getClient()

        // Fetch subscription status
        const subscriptionResponse = await apolloClient.query({
          query: subscriptionStatus,
          variables: { deviceId: this.deviceId },
          context: { headers: { 'X-Public-Token': token } },
        })

        if (subscriptionResponse?.data?.subscriptionStatus) {
          this.subscriptionData = subscriptionResponse.data.subscriptionStatus
        }

        // Fetch billing info
        const billingResponse = await apolloClient.query({
          query: deviceBillingInfo,
          variables: { deviceId: this.deviceId },
          context: { headers: { 'X-Public-Token': token } },
        })

        if (billingResponse?.data?.billingInfo) {
          const billing = billingResponse.data.billingInfo
          // Extract phone prefix and number
          const phoneMatch = billing.phone?.match(/^(\+\d+)\s*(\d+)$/)
          const phonePrefix = phoneMatch ? phoneMatch[1] : '+420'
          const phoneNumber = phoneMatch ? phoneMatch[2] : ''

          this.form = {
            firstName: billing.firstName || '',
            lastName: billing.lastName || '',
            company: billing.company || '',
            email: billing.email || '',
            phonePrefix: phonePrefix,
            phoneNumber: phoneNumber,
            country: billing.country || 'CZ',
            street: billing.street || '',
            streetNumber: billing.streetNumber || '',
            apartmentNumber: billing.apartmentNumber || '',
            zipCode: billing.zipCode || '',
            city: billing.city || '',
            companyId: billing.companyId || '',
            vat: billing.vat || '',
          }

          // Fetch available currencies from billing info
          this.prices = billing.prices || []
          this.availableCurrencies = this.prices.map((price) => price.currency)

          // Extract device name
          this.deviceName = billing.deviceName || ''

          if (this.availableCurrencies.length > 0) {
            this.selectedCurrency = this.availableCurrencies[0]
            this.updateDisplayedPrice()
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        this.error = this.$t('global.dataLoadError')
      } finally {
        this.loading = false
      }
    },

    updateDisplayedPrice() {
      const priceObj = this.prices.find((p) => p.currency === this.selectedCurrency)
      if (priceObj) {
        this.displayedPrice = (priceObj.price / 100).toFixed(2)
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.error = ''

      try {
        const paymentData = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          company: this.form.company,
          phone: this.form.phonePrefix + this.form.phoneNumber,
          country: this.form.country,
          street: this.form.street,
          streetNumber: this.form.streetNumber,
          apartmentNumber: this.form.apartmentNumber,
          zipCode: this.form.zipCode,
          city: this.form.city,
          email: this.form.email || '',
          companyId: this.form.companyId || '',
          vat: this.form.vat || '',
          currency: this.selectedCurrency,
        }

        const apolloClient = this.$apollo.getClient()

        const { data } = await apolloClient.mutate({
          mutation: subscriptionRenew,
          variables: {
            paymentData,
            deviceId: this.deviceId,
          },
          context: {
            headers: {
              'X-Public-Token': this.token,
            },
          },
        })

        if (data?.initTransaction) {
          const response = data.initTransaction

          if (response.code === '0') {
            // Success - redirect to payment gateway
            window.location.href = response.redirect
          } else {
            // Error response from mutation
            this.error = response.message || this.$t('global.transaction.paymentError')
          }
        } else {
          this.error = this.$t('global.transaction.paymentError')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.error = error.message || this.$t('global.transaction.paymentError')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
  width: 100%;
  /* background-color: #f9f9f9; */
  padding: 40px 20px 80px;
  font-family: 'Open Sans', Arial, sans-serif;
}

.eledio-theme {
  --eledio-blue: #0073e6;
  --eledio-text: #333333;
  --eledio-light-gray: #f2f2f2;
  --eledio-border: #e0e0e0;
}

.site-header {
  margin-bottom: 32px;
}

.eledio-logo {
  height: 40px;
}

.payment-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
  margin-bottom: 40px;
}

.section-title {
  font-size: 20px;
  color: var(--eledio-text);
  margin-bottom: 24px;
  border-bottom: 1px solid var(--eledio-border);
  padding-bottom: 12px;
}

/* Keeping subscription info section unchanged */
.subscription-details {
  border-left: 4px solid #1976d2 !important;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  background: #4caf50;
  color: white;
  font-size: 14px;
  text-align: center;
}

.status-badge.expired {
  background: #f44336;
}

/* Form styling */
.billing-form {
  padding: 0 12px;
}

/* Price section styling */
.price-section {
  background-color: var(--eledio-light-gray);
  border-radius: 8px;
}

.currency-toggle {
  display: inline-flex;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.currency-btn {
  padding: 0 16px;
  min-width: 60px;
  background-color: white !important;
  border: 1px solid var(--eledio-border);
}

.currency-btn.v-btn--active {
  background-color: var(--eledio-blue) !important;
  color: white !important;
}

.price-display {
  font-size: 24px;
  font-weight: 500;
}

.price-label {
  margin-right: 12px;
  color: #666;
}

.price-value {
  color: var(--eledio-blue);
  font-size: 28px;
  font-weight: 600;
}

/* Language selector */
.language-selector {
  width: 80px;
}

/* Submit button styling */
.payment-button {
  background-color: var(--eledio-blue) !important;
  box-shadow: 0 4px 10px rgba(0, 115, 230, 0.3) !important;
  transition: transform 0.2s, box-shadow 0.2s !important;
  font-weight: 600 !important;
}

.payment-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 115, 230, 0.4) !important;
}

/* Footer */
.footer {
  color: #666;
  font-size: 14px;
}

.copyright {
  margin: 0;
}

/* Input field styling */
:deep(.v-input--dense .v-input__slot) {
  min-height: 44px !important;
}

:deep(.v-text-field--filled .v-input__slot),
:deep(.v-select--filled .v-input__slot) {
  background-color: var(--eledio-light-gray) !important;
}

:deep(.v-text-field--rounded) {
  border-radius: 8px !important;
}

:deep(.v-label) {
  color: #555 !important;
  font-size: 14px !important;
}

/* Responsive styling */
@media (max-width: 600px) {
  .fill-height {
    padding: 20px 12px 80px;
  }

  .payment-card {
    padding: 16px !important;
  }

  .price-value {
    font-size: 24px;
  }
}
</style>
